import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getFinanzasDashboard } from "../../../js/finanzasService";

import {    SkeletonFinanzasTable,
    SkeletonKPICard,
    SkeletonFilters,
    EmptyState,
    ErrorState
} from "../../../components/ui/Admin/DashboardComponents/LoadingSkeletons/SkeletonKPICard";

import FinanzasFilters from "../../../components/ui/Admin/FinanzasComponents/FinanzasFilters";
import FinanzasKPICards from "../../../components/ui/Admin/FinanzasComponents/FinanzasKPICards";
import FinanzasTable from "../../../components/ui/Admin/FinanzasComponents/FinanzasTable";
import ReporteFinanzasExcel from "../../../components/ui/Admin/FinanzasComponents/ReporteFinanzasExcel";
import Paginacion from "../../../components/Reutilizables/Paginacion";
import FetchWithGif from "../../../components/Reutilizables/FetchWithGif";

export default function FinanzasUI({ apiBasePath = '/api/admin' }) {
    const [filters, setFilters] = useState({
        fecha_inicio: "",
        fecha_fin: "",
        abonado_por: "",
    });

    const [uiErrors, setUiErrors] = useState({});
    const [serverErrorMsg, setServerErrorMsg] = useState("");
    const [state, setState] = useState({
        loading: false,
        error: "",
        pagos: [],
        resumen: null,
        meta: {},
        initialLoad: true,
    });
    const [isLoadingExport, setIsLoadingExport] = useState(false);
    const excelDataRef = useRef({ rows: [], meta: {}, isGenerating: false });
    const validateFilters = useCallback((f) => {
        const errors = {};
        const iso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s);

        if (!f.fecha_inicio) errors.fecha_inicio = "La fecha de inicio es obligatoria.";
        else if (!iso(f.fecha_inicio)) errors.fecha_inicio = "Formato inválido.";

        if (!f.fecha_fin) errors.fecha_fin = "La fecha de fin es obligatoria.";
        else if (!iso(f.fecha_fin)) errors.fecha_fin = "Formato inválido.";

        if (f.fecha_inicio && f.fecha_fin && iso(f.fecha_inicio) && iso(f.fecha_fin)) {
            if (new Date(f.fecha_fin) < new Date(f.fecha_inicio)) {
                errors.fecha_fin = "La fecha de fin no puede ser menor a la de inicio.";
            }
        }

        return errors;
    }, []);

    const handle422 = useCallback((payload) => {
        const backendErrors = payload?.errors || {};
        const mapped = {};

        if (backendErrors.fecha_inicio) mapped.fecha_inicio = backendErrors.fecha_inicio[0];
        if (backendErrors.fecha_fin) mapped.fecha_fin = backendErrors.fecha_fin[0];
        setUiErrors(mapped);
        setServerErrorMsg(payload?.message || "Revise los campos marcados.");
    }, []);

    const fetchData = useCallback(
        async (page = 1, initial = false) => {
            setState((s) => ({ ...s, loading: true, initialLoad: initial }));
            setServerErrorMsg("");

            try {
                const payload = { ...filters, page };
                const response = await getFinanzasDashboard(payload, apiBasePath);

                if (!response.success) {
                    throw new Error(response.message || "Error al cargar datos");
                }

                setState((prev) => ({
                    ...prev,
                    loading: false,
                    error: "",
                    pagos: response.data || [],
                    resumen: response.meta || null,
                    meta: response.meta || {},
                    initialLoad: false,
                }));
            } catch (error) {
                const status = error?.status || error?.response?.status;
                const backendPayload = error?.response?.data || {};

                if (status === 422) {
                    handle422(backendPayload);
                    setState((prev) => ({ ...prev, loading: false, initialLoad: false }));
                    return;
                }

                const msg = error.message || "No se pudieron cargar los datos";
                setState((prev) => ({
                    ...prev,
                    loading: false,
                    error: msg,
                    initialLoad: false,
                }));
                setServerErrorMsg(msg);
            }
        },
        [filters, handle422, apiBasePath]
    );

    useEffect(() => {
        setState((s) => ({ ...s, initialLoad: false }));
    }, []);

    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
        setUiErrors((prev) => ({ ...prev, [name]: undefined }));
    }, []);

    const handleApply = useCallback(() => {
        const errors = validateFilters(filters);
        setUiErrors(errors);

        if (Object.keys(errors).length > 0) {
            setServerErrorMsg("Por favor, corrija los errores en las fechas.");
            return;
        }

        fetchData(1, true);
    }, [filters, validateFilters, fetchData]);

    const handleReset = useCallback(() => {
        setFilters({
            fecha_inicio: "",
            fecha_fin: "",
            abonado_por: "",
        });
        setUiErrors({});
        setServerErrorMsg("");
        setState((prev) => ({
            ...prev,
            pagos: [],
            resumen: null,
            meta: {},
        }));
    }, []);

    const handlePageChange = useCallback(
        (newPage) => {
            fetchData(newPage, false);
            window.scrollTo({ top: 0, behavior: "smooth" });
        },
        [fetchData]
    );

    const handleExportExcel = useCallback(async () => {
        if (excelDataRef.current.isGenerating) return;

        const errors = validateFilters(filters);
        setUiErrors(errors);

        if (Object.keys(errors).length > 0) {
            setServerErrorMsg("Por favor, corrija los errores en las fechas antes de exportar.");
            return;
        }

        setIsLoadingExport(true);
        excelDataRef.current.isGenerating = true;

        try {
            const payload = { ...filters, get_all: true };
            const response = await getFinanzasDashboard(payload, apiBasePath);

            if (!response.success) {
                throw new Error(response.message || "Error al cargar datos para Excel");
            }

            excelDataRef.current = {
                rows: response.data || [],
                meta: response.meta || {},
                isGenerating: true,
            };
        } catch (error) {
            console.error("Error al exportar Excel:", error);
            excelDataRef.current.isGenerating = false;
            setServerErrorMsg(error.message || "No se pudo generar el Excel.");
        } finally {
            setIsLoadingExport(false);
        }
    }, [filters, validateFilters, apiBasePath]);

    const handleExcelComplete = useCallback(() => {
        excelDataRef.current = { rows: [], meta: {}, isGenerating: false };
    }, []);

    const handleExcelGenerated = useCallback((url) => {
        const link = document.createElement("a");
        link.href = url;
        const fileName = `Reporte_Finanzas_${filters.fecha_inicio}_${filters.fecha_fin}.xlsx`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [filters.fecha_inicio, filters.fecha_fin]);

    const normalizedMeta = useMemo(() => ({
        current_page: state.meta?.current_page ?? 1,
        last_page: state.meta?.last_page ?? 1,
        from: state.meta?.from ?? 0,
        to: state.meta?.to ?? 0,
        per_page: state.meta?.per_page ?? 50,
        total: state.meta?.total_registros ?? state.meta?.total ?? 0,
    }), [state.meta]);

    const kpis = useMemo(() => ({
        total_monto_abonado: Number(state.resumen?.total_monto_abonado ?? 0),
        total_pagos: Number(state.resumen?.total_pagos ?? 0),
        monto_caja_chica: Number(state.resumen?.monto_caja_chica ?? 0),
        pagos_caja_chica: Number(state.resumen?.pagos_caja_chica ?? 0),
        monto_cuenta_corriente: Number(state.resumen?.monto_cuenta_corriente ?? 0),
        pagos_cuenta_corriente: Number(state.resumen?.pagos_cuenta_corriente ?? 0),
    }), [state.resumen]);

    const { loading, error, pagos, resumen, initialLoad } = state;
    const hasData = pagos && pagos.length > 0;

    const renderInitialSkeleton = () => (
        <div className="min-h-screen bg-gray-50">
            <div className="px-6 py-6 max-w-7xl mx-auto">
                <SkeletonFilters />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <SkeletonKPICard key={i} />
                    ))}
                </div>
                <SkeletonFinanzasTable />
            </div>
        </div>
    );

    if (loading && initialLoad) return renderInitialSkeleton();

    if (error && !hasData)
        return (
            <ErrorState
                title="Error al cargar el dashboard de finanzas"
                message={error}
                onRetry={() => fetchData(1, true)}
            />
        );

    return (
        <div className="min-h-screen bg-gray-50">
            {loading && !initialLoad && <FetchWithGif />}

            <div className="px-6 py-6 max-w-7xl mx-auto space-y-6">

                <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                    <div className="p-6 md:p-7">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-red-500">Control financiero</p>
                            <h1 className="mt-2 text-2xl font-bold text-slate-900">Dashboard de pagos y caja</h1>
                            <p className="mt-3 max-w-2xl text-sm text-slate-600">
                                Revisión de pagos realizados y comportamiento por origen de abono dentro del periodo consultado.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
                    <div className="mb-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Filtros de conciliación</p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">Periodo y origen de abono</h2>
                    </div>
                    <FinanzasFilters
                        filters={filters}
                        onChange={handleFilterChange}
                        onApply={handleApply}
                        onReset={handleReset}
                        loading={loading}
                        errors={uiErrors}
                        formError={serverErrorMsg}
                    />
                </section>

                {resumen && <FinanzasKPICards resumen={kpis} />}
                {hasData ? (
                    <>
                        <div className="mt-4 flex justify-end">
                            <button
                                type="button"
                                onClick={handleExportExcel}
                                disabled={isLoadingExport || loading || pagos.length === 0}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                                    isLoadingExport || loading || pagos.length === 0
                                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        : "bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg"
                                }`}
                            >
                                {isLoadingExport ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Generando Excel...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        Generar Excel
                                    </>
                                )}
                            </button>
                        </div>
                        <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
                            <FinanzasTable pagos={pagos} isLoading={loading && !initialLoad} />
                        </div>

                        {normalizedMeta.last_page > 1 && (
                            <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
                                <Paginacion
                                    paginacion={{
                                        pagina_actual: normalizedMeta.current_page,
                                        ultima_pagina: normalizedMeta.last_page,
                                        desde: normalizedMeta.from,
                                        hasta: normalizedMeta.to,
                                        total: normalizedMeta.total,
                                    }}
                                    alCambiarPagina={handlePageChange}
                                    nombreItem="pagos"
                                    colorTema="red"
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <EmptyState
                        title="No hay datos de pagos realizados"
                        description="No se encontraron cuotas canceladas para los filtros seleccionados"
                    />
                )}

                {excelDataRef.current.isGenerating && (
                    <ReporteFinanzasExcel
                        rows={excelDataRef.current.rows}
                        filters={filters}
                        onComplete={handleExcelComplete}
                        onExcelGenerated={handleExcelGenerated}
                    />
                )}
            </div>
        </div>
    );
}
